import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { XMarkIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { Switch } from "@headlessui/react";
import DialogLayout from "./DialogLayout";
import { renderFieldIcon } from "../fields/FieldList";
import type { ExportFormat } from "../api/DataApi";

type ExportCSVDialogProps = {
  visible: boolean;
  contentType: string;
  onClose: () => void;
  onExport: (selectedColumns: string[], format: ExportFormat) => void;
  exporting?: boolean;
};

const ExportCSVDialog = ({
  visible,
  contentType,
  onClose,
  onExport,
  exporting = false,
}: ExportCSVDialogProps) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(new Set());
  const [fileFormat, setFileFormat] = useState<ExportFormat>("csv");

  // Get project fields from Redux store
  const projectFields = useSelector(
    (state: any) => state.projectReducer.getIn(["project", "fields"]) || []
  );

  // Activity-related field IDs to hide from export
  const activityFieldIds = new Set([
    "firstComment",
    "lastComment",
    "firstMeeting",
    "lastMeeting",
    "firstCall",
    "lastCall",
    "firstTask",
    "lastTask",
    "firstDeadline",
    "lastDeadline",
    "firstEmail",
    "lastEmail",
    "firstMeetingInPerson",
    "lastMeetingInPerson",
    "firstActivity",
    "lastActivity",
  ]);

  // Filter fields by content type and prepare for display
  const availableFields = useMemo(() => {
    if (!projectFields || projectFields.size === 0) return [];

    const formType = contentType === "COMPANY" ? "COMPANY" : contentType;
    return projectFields
      .filter((field: any) => field.get("formType") === formType)
      .filter((field: any) => !activityFieldIds.has(field.get("id")))
      .map((field: any) => ({
        id: field.get("id"),
        label: field.get("label"),
        type: field.get("type"),
        formType: field.get("formType"),
        isCustomField: field.get("isCustomField") || false,
      }))
      .toJS();
  }, [projectFields, contentType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter fields based on search query
  const filteredFields = useMemo(() => {
    if (!searchQuery.trim()) return availableFields;
    const query = searchQuery.toLowerCase();
    return availableFields.filter(
      (field: any) =>
        field.label?.toLowerCase().includes(query) || field.id?.toLowerCase().includes(query)
    );
  }, [availableFields, searchQuery]);

  useEffect(() => {
    if (visible) {
      const storedFormat = localStorage.getItem("export_file_format");
      if (storedFormat === "csv" || storedFormat === "xlsx") {
        setFileFormat(storedFormat);
      }
    }
  }, [visible]);

  // Initialize with all columns selected when dialog opens
  useEffect(() => {
    if (visible && availableFields.length > 0) {
      const storedSelection = localStorage.getItem(`export_columns_${contentType}`);
      if (storedSelection) {
        try {
          const parsed = JSON.parse(storedSelection);
          // Only use stored if fields still exist
          const validIds = new Set(availableFields.map((f: any) => f.id));
          const validSelection = parsed.filter((id: string) => validIds.has(id));
          if (validSelection.length > 0) {
            setSelectedColumns(new Set(validSelection));
            return;
          }
        } catch {
          // Invalid stored data, use defaults
        }
      }
      // Default: select all
      setSelectedColumns(new Set(availableFields.map((f: any) => f.id)));
    }
  }, [visible, availableFields, contentType]);

  // Toggle single column
  const toggleColumn = (fieldId: string) => {
    setSelectedColumns((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(fieldId)) {
        newSet.delete(fieldId);
      } else {
        newSet.add(fieldId);
      }
      return newSet;
    });
  };

  // Select all columns
  const selectAll = () => {
    setSelectedColumns(new Set(availableFields.map((f: any) => f.id)));
  };

  // Deselect all columns
  const deselectAll = () => {
    setSelectedColumns(new Set());
  };

  // Handle export
  const handleExport = () => {
    if (selectedColumns.size === 0) return;

    // Save selection to localStorage
    localStorage.setItem(
      `export_columns_${contentType}`,
      JSON.stringify(Array.from(selectedColumns))
    );
    localStorage.setItem("export_file_format", fileFormat);

    // Preserve order based on availableFields
    const orderedColumns = availableFields
      .filter((f: any) => selectedColumns.has(f.id))
      .map((f: any) => f.id);

    onExport(orderedColumns, fileFormat);
  };

  const getContentTypeLabel = () => {
    switch (contentType) {
      case "SALE":
        return t("Deals");
      case "CONTACT":
        return t("Contacts");
      case "COMPANY":
        return t("Companies");
      default:
        return contentType;
    }
  };

  return (
    <DialogLayout show={visible}>
      <div className="w-[480px] max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {t("Export data")} — {getContentTypeLabel()}
          </h2>
          <button
            onClick={onClose}
            className="p-1 rounded-md hover:bg-gray-100 transition-colors"
            disabled={exporting}
          >
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Search and Actions */}
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="relative mb-3">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("Search columns...")}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button
                onClick={selectAll}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                {t("Select All")}
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={deselectAll}
                className="text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                {t("Deselect All")}
              </button>
            </div>
            <span className="text-sm text-gray-500">
              {selectedColumns.size} / {availableFields.length} {t("columns")}
            </span>
          </div>
        </div>

        {/* Column List */}
        <div className="flex-1 overflow-y-auto px-4 py-2 min-h-[200px] max-h-[400px]">
          {filteredFields.length === 0 ? (
            <div className="text-center py-8 text-gray-500">{t("No columns found")}</div>
          ) : (
            <div className="space-y-1">
              {filteredFields.map((field: any) => (
                <div
                  key={field.id}
                  className="flex items-center justify-between py-2 px-2 hover:bg-gray-50 rounded-lg cursor-pointer"
                  onClick={() => toggleColumn(field.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      {renderFieldIcon(field, "w-4 h-4 text-gray-500")}
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-700">{field.label}</div>
                      {field.isCustomField && (
                        <span className="text-xs text-gray-400">{t("Custom Field")}</span>
                      )}
                    </div>
                  </div>
                  <Switch
                    checked={selectedColumns.has(field.id)}
                    onChange={() => toggleColumn(field.id)}
                    className="group relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-none data-checked:bg-blue-600"
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  >
                    <span
                      aria-hidden="true"
                      className="pointer-events-none inline-block size-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out group-data-checked:translate-x-4"
                    />
                  </Switch>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-100">
          <div className="text-xs font-medium text-gray-500 mb-2">{t("File format")}</div>
          <div className="flex rounded-lg border border-gray-200 p-1 gap-1">
            <button
              type="button"
              onClick={() => setFileFormat("csv")}
              disabled={exporting}
              className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                fileFormat === "csv"
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-gray-700 hover:bg-gray-50"
              } disabled:opacity-50`}
            >
              CSV
            </button>
            <button
              type="button"
              onClick={() => setFileFormat("xlsx")}
              disabled={exporting}
              className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                fileFormat === "xlsx"
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-gray-700 hover:bg-gray-50"
              } disabled:opacity-50`}
            >
              {t("Excel (.xlsx)")}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-4 py-3 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            disabled={exporting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50"
          >
            {t("Cancel")}
          </button>
          <button
            onClick={handleExport}
            disabled={selectedColumns.size === 0 || exporting}
            className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {exporting ? (
              <span className="flex items-center gap-2">
                <span className="inline-block w-4 h-4 border-2 border-white/60 border-t-white rounded-full animate-spin"></span>
                {t("Exporting...")}
              </span>
            ) : (
              t("Export")
            )}
          </button>
        </div>
      </div>
    </DialogLayout>
  );
};

export default ExportCSVDialog;
